import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridFooterContainer,
  GridFooter,
  GridPagination,
} from "@mui/x-data-grid";
import Typography from "@mui/material/Typography";
import ExcelSVG from "../../ExcelSVG.svg";
import FilterSVG from "../../FilterSVG.svg";
import * as XLSX from "xlsx";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { API_URL } from "../components/api";
import AuthContext from "../context/AuthContext";
import "./ZoneDashboard.css"; // Add your custom styles here

function ZoneDashboard() {
  const { user } = useContext(AuthContext);
  const [zones, setZones] = useState([]); // State to store fiber zones
  const [selectedZone, setSelectedZone] = useState(""); // State to store the selected zone
  const [clientActiveCount, setClientActiveCount] = useState(0); // State for active client count
  const [clientInactiveCount, setClientInactiveCount] = useState(0); // State for inactive client count
  const [buildingActiveCount, setBuildingActiveCount] = useState(0); // State for active building count
  const [buildingReadyCount, setBuildingReadyCount] = useState(0); // State for ready building count
  const [subscriptionsOnlineCount, setSubscriptionsOnlineCount] = useState(0); // State for online subscription count
  const [subscriptionsOfflineCount, setSubscriptionsOfflineCount] = useState(0); // State for offline subscription count
  const [loading, setLoading] = useState(false); // State to manage loading state
  const [data, setData] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    username: "",
    fullName: "",
    code: "",
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState({
    items: [],
  });

  const [searchInput, setSearchInput] = useState(""); // To capture search input

  // NEW: State to track column visibility
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [searchFilterTimeout, setSearchFilterTimeout] = useState(null);

  const authTokens = JSON.parse(localStorage.getItem("authTokens"));

  // Fetch fiber zones on component mount
  useEffect(() => {
    const fetchZones = async () => {
      try {
        const authTokens = JSON.parse(localStorage.getItem("authTokens"));
        const response = await axios.get(`${API_URL}/radius/fiber/zone/`, {
          headers: { Authorization: `Bearer ${authTokens.access}` },
          params: { username: user.username },
        });
        setZones(response.data); // Set the fetched zones
      } catch (error) {
        console.error("Error fetching fiber zones:", error);
      }
    };

    fetchZones();
  }, [user.username]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = `${API_URL}/radius/fiber/zone/subscriptions/`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
          params: {
            username: user.username,
            zone: selectedZone, // Always include zone, even if it's "All"
          },
        });

        if (response.status === 200) {
          setData(response.data);
          console.log(response.data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.username, authTokens.access, selectedZone]);

  const getStatusCellStyle = (params) => {
    let statusColor = "";
    switch (params.value) {
      case "offline":
        statusColor = "status-offline";
        break;
      case "online":
        statusColor = "status-online";
        break;
      case "inactive":
        statusColor = "status-inactive";
        break;
      case "expired":
        statusColor = "status-expired";
        break;
      case "hold":
        statusColor = "status-hold";
        break;
      default:
        break;
    }
    return statusColor;
  };

  const columns = [
    {
      field: "status",
      headerName: "Status",
      width: 70,
      cellClassName: (params) => getStatusCellStyle(params), // Apply cell style based on status
    },
    { field: "username", headerName: "Username", minWidth: 100 },
    {
      field: "fullname_en",
      headerName: "Full Name",
      flex: 2,
      minWidth: 250,
    },
    { field: "code", headerName: "Code", minWidth: 100 },
  ];

  const getRowId = (row) => row.subscription_id;

  // Create a delayed search filter function
  const delaySearchFilter = (input) => {
    setSearchQuery(input);
  };

  useEffect(() => {
    // Save "searchValue" value before clearing other session storage items
    const searchValue = sessionStorage.getItem("searchZoneDashboard");

    // Clear all session storage items except "searchValue"
    Object.keys(sessionStorage).forEach((key) => {
      if (key !== "searchZoneDashboard") {
        sessionStorage.removeItem(key);
      }
    });

    // Restore "searchValue" value if needed
    if (searchValue) {
      sessionStorage.setItem("searchZoneDashboard", searchValue);
      setSearchQuery(searchValue);
      setSearchInput(searchValue); // Sync the searchInput with the restored value
    }
  }, []);

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchInput(query);
    sessionStorage.setItem("searchZoneDashboard", query);

    if (searchFilterTimeout) {
      clearTimeout(searchFilterTimeout);
    }

    const newTimeout = setTimeout(() => {
      delaySearchFilter(query);
    }, 100);
    setSearchFilterTimeout(newTimeout);
  };

  // IMPROVED: Combined filtering logic that works for both display AND export
  const filteredRows = data.filter((row) => {
    // Apply search query filter
    const searchMatch = searchQuery
      .split(" ")
      .every((word) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(word.toLowerCase().trim())
        )
      );

    // Apply modal filters
    const modalFilterMatch = Object.keys(filters).every((key) => {
      if (!filters[key]) return true;

      if (key === "connectivity_dependent") {
        const filterValue = filters[key].toLowerCase();
        if (row.connectivity === "Fiber") {
          return (row.code || "").toLowerCase().includes(filterValue);
        } else if (row.connectivity === "Wireless") {
          return (row.name || "").toLowerCase().includes(filterValue);
        }
        return false;
      }

      const rowValue = String(row[key] || "").toLowerCase();
      const filterValue = String(filters[key]).toLowerCase();
      return rowValue.includes(filterValue);
    });

    // Apply DataGrid filters
    const gridFilterMatch = gridFilterModel.items.every((filterItem) => {
      if (!filterItem.value) return true;

      const cellValue = row[filterItem.field];
      const filterValue = filterItem.value;

      switch (filterItem.operator) {
        case "contains":
          return String(cellValue || "")
            .toLowerCase()
            .includes(String(filterValue).toLowerCase());
        case "equals":
          return String(cellValue || "") === String(filterValue);
        case "startsWith":
          return String(cellValue || "")
            .toLowerCase()
            .startsWith(String(filterValue).toLowerCase());
        case "endsWith":
          return String(cellValue || "")
            .toLowerCase()
            .endsWith(String(filterValue).toLowerCase());
        default:
          return true;
      }
    });

    return searchMatch && modalFilterMatch && gridFilterMatch;
  });

  const handleFilterChange = (field, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const columnHeaders = {
    status: "Status",
    username: "Username",
    fullname_en: "Full Name",
    code: "Code",
  };

  // Format date fields properly for export — e.g. "21/02/2025 10:25:52 AM"
  const formatDateForExport = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);

    // Format as DD/MM/YYYY HH:MM:SS AM/PM
    const formattedDate = date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    // Ensure consistent spacing before AM/PM
    return formattedDate.replace(" am", " AM").replace(" pm", " PM");
  };

  /// Corrected: Transform rows respecting column visibility
  const transformRowsForExport = (rows) => {
    return rows.map((row) => {
      const transformedRow = {};

      Object.keys(columnHeaders).forEach((field) => {
        // Only export if column is visible
        if (columnVisibilityModel[field] !== false) {
          const headerName = columnHeaders[field];

          // Default: use the value or empty string
          transformedRow[headerName] = row[field] ?? "";
        }
      });

      return transformedRow;
    });
  };

  // IMPROVED: Export function that uses the already-filtered rows
  const handleExportToExcel = () => {
    console.log("Exporting data...");
    console.log("Total data rows:", data.length);
    console.log("Filtered rows:", filteredRows.length);
    console.log("Active filters:", {
      search: searchQuery,
      modalFilters: filters,
      gridFilters: gridFilterModel.items,
    });
    console.log("Column visibility:", columnVisibilityModel);

    // Always use filteredRows which has ALL filters applied
    const transformedRows = transformRowsForExport(filteredRows);

    console.log("Rows to export:", transformedRows.length);

    const worksheet = XLSX.utils.json_to_sheet(transformedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "FiberZoneSubscriptions");
    XLSX.writeFile(workbook, "FiberZoneSubscriptions.xlsx");
  };

  const [isPressed, setIsPressed] = useState(false);

  const handleExportClick = () => {
    setIsPressed(true);
    handleExportToExcel();
    setTimeout(() => {
      setIsPressed(false);
    }, 200);
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <Button
        onClick={() => setShowFilterModal(true)}
        variant="outline-primary"
        style={{
          border: "none",
        }}
      >
        <img
          src={FilterSVG}
          alt="Filter Icon"
          style={{
            color: "white",
            width: "18px",
            marginRight: "8px",
            filter: "invert(0)", // keep black icon
          }}
        />
        Filter
      </Button>
      {/*<GridToolbarFilterButton />*/}
      <GridToolbarExport />
      <Button
        onClick={handleExportClick}
        variant="outline-success"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "5px 10px",
          fontSize: "14px",
          border: "none",
          backgroundColor: isPressed ? "white" : "transparent",
          color: isPressed ? "black" : "#007bff",
          cursor: "pointer",
        }}
      >
        <img
          src={ExcelSVG}
          alt="Excel Icon"
          style={{
            width: "20px",
            marginRight: "8px",
            filter: isPressed ? "invert(0)" : "invert(1)",
          }}
        />
        Excel
      </Button>
    </GridToolbarContainer>
  );

  useEffect(() => {
    if (selectedZone) {
      const fetchCounts = async () => {
        setLoading(true);
        try {
          const authTokens = JSON.parse(localStorage.getItem("authTokens"));

          // Fetch client active count
          const clientActiveResponse = await axios.get(
            `${API_URL}/administration/clients/count/`,
            {
              headers: { Authorization: `Bearer ${authTokens.access}` },
              params: {
                zone: selectedZone,
                status: "active",
              },
            }
          );
          setClientActiveCount(clientActiveResponse.data.Client_Active);

          // Fetch client inactive count
          const clientInactiveResponse = await axios.get(
            `${API_URL}/administration/clients/count/`,
            {
              headers: { Authorization: `Bearer ${authTokens.access}` },
              params: {
                zone: selectedZone,
                status: "inactive",
              },
            }
          );
          setClientInactiveCount(clientInactiveResponse.data.Client_Inactive);

          // Fetch building active count
          const buildingActiveResponse = await axios.get(
            `${API_URL}/administration/buildings/count/`,
            {
              headers: { Authorization: `Bearer ${authTokens.access}` },
              params: {
                zone: selectedZone,
                status: "active",
              },
            }
          );
          setBuildingActiveCount(buildingActiveResponse.data.Building_Active);

          // Fetch building ready count
          const buildingReadyResponse = await axios.get(
            `${API_URL}/administration/buildings/count/`,
            {
              headers: { Authorization: `Bearer ${authTokens.access}` },
              params: {
                zone: selectedZone,
                status: "ready",
              },
            }
          );
          setBuildingReadyCount(buildingReadyResponse.data.Building_Ready);

          // Fetch subscription online count
          const subscriptionOnlineResponse = await axios.get(
            `${API_URL}/radius/fiber/zone/count/`,
            {
              headers: { Authorization: `Bearer ${authTokens.access}` },
              params: {
                zone: selectedZone,
                status: "online",
              },
            }
          );
          setSubscriptionsOnlineCount(
            subscriptionOnlineResponse.data.Subscription_Online
          );

          // Fetch subscription offline count
          const subscriptionOfflineResponse = await axios.get(
            `${API_URL}/radius/fiber/zone/count/`,
            {
              headers: { Authorization: `Bearer ${authTokens.access}` },
              params: {
                zone: selectedZone,
                status: "offline",
              },
            }
          );
          setSubscriptionsOfflineCount(
            subscriptionOfflineResponse.data.Subscription_Offline
          );
        } catch (error) {
          console.error("Error fetching counts:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchCounts();
    }
  }, [selectedZone]);

  return (
    <div>
      <div className="zone-dashboard">
        <div
          className="zone-dashboard-header"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <h1 style={{ color: "white" }}>Zone Dashboard</h1>

          {/* Zone Selector */}
          <div className="zone-selector">
            <label htmlFor="zone-select" style={{ color: "white" }}>
              Select Zone:
            </label>
            <select
              id="zone-select"
              value={selectedZone}
              onChange={(e) => setSelectedZone(e.target.value)}
              disabled={loading}
            >
              <option value="">Select a zone</option>
              <option value="All">All</option>
              {zones.map((zone, index) => (
                <option key={index} value={zone.zone_name}>
                  {zone.zone_name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {/* Cards Displaying Counts */}
        <div className="cards-container">
          {/* Client Active Count Card */}
          <div className="stats-card">
            <h3>Active Clients</h3>
            <div className="card-value">{clientActiveCount}</div>
          </div>

          {/* Client Inactive Count Card */}
          <div className="stats-card">
            <h3>Inactive Clients</h3>
            <div className="card-value">{clientInactiveCount}</div>
          </div>

          {/* Building Active Count Card */}
          <div className="stats-card">
            <h3>Active Buildings</h3>
            <div className="card-value">{buildingActiveCount}</div>
          </div>

          {/* Building Ready Count Card */}
          <div className="stats-card">
            <h3>Ready Buildings</h3>
            <div className="card-value">{buildingReadyCount}</div>
          </div>

          {/* Subscription Online Count Card */}
          <div className="stats-card">
            <h3>Subscription Online</h3>
            <div className="card-value">{subscriptionsOnlineCount}</div>
          </div>

          {/* Subscription Offline Count Card */}
          <div className="stats-card">
            <h3>Subscription Offline</h3>
            <div className="card-value">{subscriptionsOfflineCount}</div>
          </div>
        </div>
      </div>
      <div className="ClientTable">
        <style>{`
        .MuiDataGrid-panelWrapper {
          background: #122632 !important;
        }
      `}</style>
        <input
          type="text"
          placeholder="Search..."
          value={searchInput}
          onChange={handleSearchChange}
          className="search-client"
        />
        {!loading && data.length > 0 && (
          <div style={{ height: 520, width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={getRowId}
              filterModel={gridFilterModel}
              onFilterModelChange={(newModel) => setGridFilterModel(newModel)}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
              }
              components={{
                Toolbar: CustomToolbar,
              }}
              rowsPerPageOptions={[10, 100, 1000]}
              pagination
              disableSelectionOnClick
            />
          </div>
        )}
        <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Filters</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {columns
              .filter((col) => col.field !== "actions")
              .map((col) => (
                <div key={col.field} className="filter">
                  <label>{col.headerName}</label>
                  <input
                    type="text"
                    placeholder={`Filter by ${col.headerName}`}
                    value={filters[col.field] || ""}
                    onChange={(e) =>
                      handleFilterChange(col.field, e.target.value)
                    }
                    className="filter-input"
                  />
                </div>
              ))}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowFilterModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}

export default ZoneDashboard;
